import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calculator, ClipboardCheck, FileText, LineChart, ShieldCheck, Sparkles } from "lucide-react";
import Layout from "../components/Layout";

const HERO_IMG =
  "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxneW0lMjBpbnRlcmlvciUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3ODQwODQ1NjN8MA&ixlib=rb-4.1.0&q=85";

const Features = [
  { icon: Calculator, title: "SDE-based", body: "Seller Discretionary Earnings — normalised earnings to one working owner. The way small gyms actually trade." },
  { icon: LineChart, title: "Interpolated multiples", body: "Graduated multiples between earnings anchors — no artificial jumps." },
  { icon: ClipboardCheck, title: "Quality adjustments", body: "Growth, owner dependence, lease, financials and equipment all considered." },
  { icon: FileText, title: "Instant PDF report", body: "Delivered to your inbox the moment you finish the questionnaire." },
];

export default function Landing() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-6 py-16 sm:py-24 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-orange-700">
              <Sparkles className="h-3.5 w-3.5" /> Australian gym owners
            </div>
            <h1 className="font-display text-5xl font-extrabold leading-[1.05] text-slate-900 sm:text-6xl">
              What is your gym <span className="text-orange-500">actually worth?</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
              A five-minute questionnaire built specifically for Australian gyms,
              fitness, yoga and pilates studios. Get an indicative market value
              range based on normalised earnings, benchmarked against similar
              businesses we have sold.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link data-testid="hero-cta-btn" to="/valuation" className="btn-primary">
                Calculate My Gym Value
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#how-it-works" className="btn-ghost">How it works</a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-orange-500" /> No obligation
              </div>
              <div>~5 minutes</div>
              <div>PDF emailed</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl border border-slate-200 shadow-lg">
              <img src={HERO_IMG} alt="Gym interior" className="h-full w-full object-cover" />
              <div className="absolute inset-x-4 bottom-4 rounded-xl border border-slate-200 bg-white/90 p-4 backdrop-blur">
                <div className="overline">Indicative range</div>
                <div className="mt-1 font-display text-2xl font-bold text-slate-900">$240k – $300k</div>
                <div className="text-xs text-slate-500">Central estimate $270,000</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="mb-10 max-w-2xl">
            <div className="overline mb-3">How it works</div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              A methodology built for real gym transactions.
            </h2>
            <p className="mt-4 text-slate-600">
              We use the same SDE-to-one-working-owner approach that Australian
              gym buyers and business brokers use — with graduated multiples and
              structured quality adjustments.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Features.map((f) => (
              <div
                key={f.title}
                className="rounded-xl border border-slate-200 bg-white p-6 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md"
              >
                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="rounded-2xl border border-slate-200 bg-slate-900 p-10 text-white sm:p-14">
          <div className="max-w-2xl">
            <div className="overline mb-3 text-orange-300">Ready to find out?</div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
              Get your indicative valuation in about five minutes.
            </h2>
            <p className="mt-4 text-slate-300">
              You&apos;ll receive a PDF report by email covering earnings, multiples,
              benchmarks, saleability and next steps.
            </p>
            <div className="mt-8">
              <Link data-testid="cta-start-btn" to="/valuation" className="btn-primary">
                Calculate My Gym Value
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
